import React from 'react';
import { CaretRightIcon } from 'assets/icons';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';
import { EstimateForCustomer } from 'containers/customers/customers.model';
import { getStatusColor } from 'containers/estimates/utils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { EstimateStatus } from '../constants';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';

interface EstimateCardInterface {
  estimate: EstimateForCustomer;
}

const EstimateCard: React.FC<EstimateCardInterface> = ({ estimate }) => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const { rootCustomerId, rootEstimateId, rootInvoiceId, rootReceiptId, rootPage, rootDisputeId, rootJobId } =
    useParams();

  const handleEstimateClick = (estimateId: string) => {
    if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/estimate/${estimateId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  return (
    <div
      className="flex w-full cursor-pointer items-end justify-between gap-1 border-b border-highlightGray pb-4"
      onClick={() => handleEstimateClick(estimate.id)}>
      <div className="flex w-[70%] flex-col gap-1">
        <div className="text-sbase leading-[18px] tracking-[-0.3px] text-primaryText">{estimate.estimateNumber}</div>
        <div className="max-w-[98%] break-words text-17px font-semibold text-primaryText">{estimate.title}</div>
        <div className="flex items-center gap-2 text-px13 leading-[17px] text-accentText">
          {DateUtils.getDateInFormat({
            date: new Date(estimate?.createdAt),
            dateFormat: 'dd MMM yyyy'
          })}
          <div className="mb-[3px] h-1 w-1 rounded-full bg-accentText" />
          <CustomAvatar
            src={estimate.createdBy.profilePicture}
            label={getInitials(estimate.createdBy.name)}
            width="w-4"
            height="h-4"
            fontStyle="text-[9px]"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="text-right">
          <div className="mb-1 text-17px font-bold tracking-[-0.3px] text-primaryText">
            {getAmountWithCurrency(estimate.amount ?? '')}
          </div>
          <div
            className="text-px13 capitalize leading-[17px]"
            style={{ color: getStatusColor(estimate.status as EstimateStatus) }}>
            {estimate.status.toLowerCase()}
          </div>
        </div>
        <CaretRightIcon className="path-fill-current shrink-0 text-gray-1" />
      </div>
    </div>
  );
};

export default EstimateCard;
